/**
 * How much of a CI queue's cohort the browser loads, and what it says when that
 * is not all of it.
 *
 * The queues filter and sort client-side — most chips read values no FHIR search
 * parameter can express (a `QuestionnaireResponse` answer, the absence of a
 * Communication) — so a chip is only truthful over rows that are actually
 * loaded. The tabs therefore load the whole date-scoped cohort rather than a
 * page, and the cap plus the notice below are what keep that honest when a range
 * is too wide to load.
 */

/**
 * Rows per request.
 *
 * 200, up from 50. Measured against staging on 2026-09-17: the entire 447-row
 * pre-consultation cohort came back in ONE request, with both of the query's
 * nested reverse-chains, no error. The 50k-point budget that forced small pages
 * was blown by the pre-hydration query's EIGHT chains per row; at two, 200 rows
 * has large headroom. Do not reintroduce per-row reverse-chains without
 * re-measuring this.
 */
export const QUEUE_PAGE_SIZE = 200;

/**
 * Rows loaded before paging stops.
 *
 * A bound on the load, not a statement about the data: past it the tab keeps
 * today's load-on-scroll behaviour and says so.
 *
 * Measured on staging, 2026-09-17 — the whole 447-row pre-consultation cohort
 * loaded in **8 requests in 2.7s with no rate limiting**: 3 root pages plus 5
 * hydration searches. Hydration cost scales with DISTINCT PATIENTS, not rows,
 * and those 447 appointments were only 128 patients (~3.5 each), so they fit in
 * one `HYDRATION_CHUNK_SIZE` chunk. Extrapolating that ratio, this cap is on the
 * order of 40 requests, not the ~120 a per-row estimate suggests.
 *
 * Production cohorts are still unmeasured (prod reads were unavailable when this
 * was written), so 2,000 stays a conservative starting value with real headroom
 * above it rather than a tuned limit. See BAR-2738.
 */
export const COHORT_ROW_CAP = 2000;

/**
 * What the notice says. Extracted from the component so the wording is testable
 * without mounting anything, and asserted structurally rather than verbatim —
 * a copy change must not turn a test red.
 */
export function cohortNoticeText(loaded: number, total: number | undefined): string {
  const tail = 'Filters and sorting apply only to these — narrow the date range to cover the whole list.';

  if (total === undefined) {
    return `Showing the first ${loaded.toLocaleString('en-US')} rows. ${tail}`;
  }

  return `Showing the first ${loaded.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} matching rows. ${tail}`;
}

/**
 * Whether the truncation notice is actually warranted.
 *
 * `cohortTruncated` is the hook reporting conservatively: it means "I stopped at
 * the cap and the last page was full", and a full last page is indistinguishable
 * from a cohort that happens to end on a page boundary. A cohort of exactly 400
 * rows with a 400 cap therefore reports as truncated, which would put a "narrow
 * the date range" warning on a list that is already complete.
 *
 * The real total settles it. When it is unavailable the notice still shows —
 * over-warning is the safe direction, since the alternative is a silently
 * partial list, which is the defect BAR-2738 exists to remove.
 */
export function shouldWarnTruncated(truncated: boolean, loaded: number, total: number | undefined): boolean {
  if (!truncated) {
    return false;
  }

  return total === undefined || total > loaded;
}
